import React from 'react';
import { motion } from 'framer-motion';
import { 
  Award, 
  ShieldCheck, 
  Clock, 
  MapPin, 
  Users, 
  CheckCircle2, 
  ArrowRight,
  Shield
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';
import { technicians } from '@/data/index';
import { TechnicianCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';

const About = () => {
  const stats = [
    { label: 'Repairs Completed', value: '15k+', icon: CheckCircle2 },
    { label: 'Years Experience', value: '14+', icon: Award },
    { label: 'Expert Technicians', value: '25+', icon: Users },
    { label: 'Service Areas', value: '12', icon: MapPin },
  ];

  const serviceAreas = [
    "Downtown Metropolitan", "Northwood Residential", "Silicon Heights", 
    "East River Industrial", "Sunset Valley", "Green Meadows", 
    "Tech Park West", "Old Town District"
  ];

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.TOOLS_EQUIPMENT_1} 
            alt="Professional tools" 
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1.5 mb-6 text-sm font-semibold tracking-wider text-primary uppercase bg-primary/10 rounded-full border border-primary/20">
              Since 2012
            </span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6 leading-tight">
              Restoring Life to Your <span className="text-primary">Essential Tech</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              We bridge the gap between complex hardware failure and seamless daily life. 
              From your living room TV to your high-performance workstation, our certified 
              technicians bring precision repair directly to your doorstep.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="rounded-full px-8">
                <Link to={ROUTE_PATHS.CONTACT}>Book a Technician</Link>
              </Button>
              <Button variant="outline" size="lg" className="rounded-full px-8">
                Our Services
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-y border-border bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex flex-col items-center text-center p-4"
              >
                <div className="p-3 bg-primary/10 rounded-xl mb-3 text-primary">
                  <stat.icon className="w-6 h-6" />
                </div>
                <div className="text-3xl font-bold mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story & Warranty Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6">The TechGuard Promise</h2>
              <div className="space-y-6">
                <p className="text-lg text-muted-foreground">
                  Founded in 2012, we realized that electronics are the heartbeat of the modern home. 
                  When they fail, life feels disrupted. Our mission was simple: provide expert, 
                  transparent, and rapid repair services without the need for customers to haul 
                  heavy equipment to a shop.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-6 rounded-2xl bg-card border border-border flex items-start gap-4">
                    <div className="mt-1 p-2 bg-primary/10 rounded-lg text-primary">
                      <Shield className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">90-Day Warranty</h4>
                      <p className="text-sm text-muted-foreground">Complete peace of mind on all parts and labor.</p>
                    </div>
                  </div>
                  <div className="p-6 rounded-2xl bg-card border border-border flex items-start gap-4">
                    <div className="mt-1 p-2 bg-primary/10 rounded-lg text-primary">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">24/7 Response</h4>
                      <p className="text-sm text-muted-foreground">Emergency IT support and call-out services.</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative aspect-square rounded-3xl overflow-hidden border-8 border-muted"
            >
              <img 
                src={IMAGES.CUSTOMER_SUPPORT_1} 
                alt="Customer support team" 
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-8 left-8 right-8 p-6 bg-background/90 backdrop-blur-md rounded-2xl border border-border shadow-2xl">
                <p className="text-sm font-medium italic text-foreground">
                  "Our technicians are not just repairmen; they are engineers who care about the 
                  longevity of your devices and the security of your data."
                </p>
                <p className="mt-4 text-xs font-bold uppercase tracking-widest text-primary">
                  — Marcus Thorne, Lead Technician
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-4xl font-bold mb-4">Meet Our Specialists</h2>
            <p className="text-muted-foreground">
              Certified experts with decades of combined experience in consumer electronics, 
              industrial appliances, and enterprise IT infrastructure.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {technicians.map((tech) => (
              <TechnicianCard key={tech.id} technician={tech} />
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Areas */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="bg-card border border-border rounded-[2.5rem] p-8 md:p-16 overflow-hidden relative">
            <div className="absolute top-0 right-0 p-12 opacity-5">
              <MapPin className="w-64 h-64" />
            </div>
            
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-8">On-Site Coverage Areas</h2>
              <p className="text-muted-foreground max-w-2xl mb-12">
                We currently provide on-site repair services across the following metropolitan areas. 
                Remote IT support and on-call diagnostics are available nationwide in 2026.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {serviceAreas.map((area, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-foreground font-medium">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    {area}
                  </div>
                ))}
              </div>

              <div className="mt-16 p-8 bg-background rounded-2xl border border-border flex flex-col md:flex-row items-center justify-between gap-8">
                <div>
                  <h3 className="text-xl font-bold mb-2">Don't see your area?</h3>
                  <p className="text-muted-foreground">We are rapidly expanding. Contact us to check availability.</p>
                </div>
                <Button asChild className="group">
                  <Link to={ROUTE_PATHS.CONTACT} className="flex items-center gap-2">
                    Check Your ZIP Code <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to fix your device?</h2>
          <p className="text-primary-foreground/80 max-w-xl mx-auto mb-10 text-lg">
            Join over 10,000 satisfied customers who trust us for their electronics 
            and IT support needs. Professional service is just a call away.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              variant="secondary" 
              className="rounded-full px-12 py-7 text-lg font-bold shadow-xl"
              asChild
            >
              <Link to={ROUTE_PATHS.CONTACT}>Schedule Repair Now</Link>
            </Button>
          </div>
          <p className="mt-8 text-sm opacity-60 font-medium tracking-wide">
            © 2026 TECH REPAIR CO. ALL RIGHTS RESERVED.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;